import { toast } from 'vue-sonner'
import {
  ONBOARDING_COMPANY_VERIFICATION_STATUS,
  ONBOARDING_QUERY_TYPES,
  VERIFICATION_PROCESS_TYPES,
  VERIFICATION_STATES,
  VERIFIED_VERIFICATION_STATES,
} from '@/constants'
import helpers from '@/utils/helpers.ts'
import { useApi } from './api'
import { useKeycloak } from './auth/keycloak'
import type { UserMeOrganization } from '@deepcloud/api-deep-admin'
import type { GetUserOrganizationResponse } from '@deepcloud/api-deep-admin-backend'

export const useOnboarding = createGlobalState(
  (currentOrganization?: MaybeRefOrGetter<UserMeOrganization | undefined>) => {
    const { user } = useKeycloak()
    const { t } = useI18n()
    const { deepAdminBackendAPI } = useApi()

    const currentOrganizationVerificationStatus = computed(
      () => toValue(currentOrganization)?.verification_state,
    )

    const currentOrganizationVerificationProcessStatus = computed(
      () => toValue(currentOrganization)?.verification_process_status,
    )

    const currentOrganizationStructure = computed(
      () => toValue(currentOrganization)?.structure,
    )

    const isCurrentOrganizationVerified = computed(
      () =>
        !!currentOrganizationVerificationStatus.value &&
        VERIFIED_VERIFICATION_STATES.includes(
          currentOrganizationVerificationStatus.value,
        ),
    )

    const isCurrentOrganizationVerifiedWeak = computed(
      () =>
        currentOrganizationVerificationStatus.value ===
        VERIFICATION_STATES.VERIFICATION_STATE_WEAK,
    )

    const isCurrentOrganizationVerifiedStrong = computed(
      () =>
        currentOrganizationVerificationStatus.value ===
        VERIFICATION_STATES.VERIFICATION_STATE_STRONG,
    )

    const isCurrentOrganizationVerificationNotStarted = computed(
      () =>
        currentOrganizationVerificationStatus.value ===
        VERIFICATION_STATES.VERIFICATION_STATE_NONE,
    )

    const shouldCallOnboardingStatus = computed(() => {
      return (
        !currentOrganizationVerificationProcessStatus.value &&
        isCurrentOrganizationVerificationNotStarted.value &&
        !!user.value?.sub
      )
    })

    const currentOrganizationOnboarding = ref<GetUserOrganizationResponse>()

    const currentOrganizationOnboardingValidationDeepID = computed(() =>
      currentOrganizationOnboarding.value?.validations?.find(
        (v) => v.source === 'deepid',
      ),
    )

    async function getUserOrganization() {
      const orgId = toValue(currentOrganization)?.group_id
      if (!user.value?.sub || !orgId) return
      try {
        const res = await deepAdminBackendAPI.value.usersOrganizations.getById(
          user.value.sub,
          orgId,
        )

        currentOrganizationOnboarding.value = res.data
      } catch {
        toast.error(t('error.error_occurred'))
      }
    }

    watchEffect(() => {
      if (shouldCallOnboardingStatus.value) {
        void getUserOrganization()
      }
    })

    const currentOrganizationOnboardingVerificationStatus = computed(
      () => currentOrganizationOnboardingValidationDeepID.value?.status,
    )

    const currentOrganizationOnboardingVerificationError = computed(
      () =>
        currentOrganizationOnboardingVerificationStatus.value ===
        ONBOARDING_COMPANY_VERIFICATION_STATUS.ONBOARDING_COMPANY_VERIFICATION_STATUS_VALIDATION_ERROR,
    )

    const isCurrentOrganizationVerificationInProgress = computed(
      () =>
        currentOrganizationVerificationProcessStatus.value ===
        VERIFICATION_PROCESS_TYPES.VERIFICATION_PROCESS_TYPE_IN_PROGRESS,
    )

    const isCurrentOrganizationPersonalSpaceVerificationInProgress = computed(
      () => {
        if (toValue(currentOrganization)) {
          return (
            isCurrentOrganizationVerificationInProgress.value &&
            helpers.isPersonalSpace(toValue(currentOrganization))
          )
        }
        return false
      },
    )

    const isCurrentOrganizationVerificationStarted = computed(
      () =>
        currentOrganizationVerificationProcessStatus.value ===
        VERIFICATION_PROCESS_TYPES.VERIFICATION_PROCESS_TYPE_STARTED,
    )

    const isCurrentOrganizationInOnboardingVerifyProcess = computed(
      () =>
        (!isCurrentOrganizationVerified.value &&
          currentOrganizationOnboardingVerificationError.value) ||
        isCurrentOrganizationVerificationInProgress.value ||
        (!currentOrganizationStructure.value &&
          isCurrentOrganizationVerificationNotStarted.value &&
          !currentOrganizationVerificationProcessStatus.value) ||
        (isCurrentOrganizationPersonal.value &&
          isCurrentOrganizationVerificationStarted.value) ||
        (!isCurrentOrganizationPersonal.value &&
          isCurrentOrganizationVerificationNotStarted.value &&
          !currentOrganizationVerificationProcessStatus.value),
    )

    const isCurrentOrganizationOnboardedServiceName = computed(
      () => toValue(currentOrganization)?.metadata?.onboarded_with,
    )

    const isCurrentOrganizationOnboardedWithDeepV = computed(
      () =>
        isCurrentOrganizationOnboardedServiceName.value ===
        ONBOARDING_QUERY_TYPES.ONBOARDING_QUERY_TYPE_DEEPV,
    )

    const isCurrentOrganizationOnboardedWithDeepVAndInProgress = computed(
      () =>
        (isCurrentOrganizationOnboardedWithDeepV.value &&
          isCurrentOrganizationVerificationInProgress.value) ||
        (isCurrentOrganizationOnboardedWithDeepVAndNone.value &&
          isCurrentOrganizationVerificationStarted.value),
    )

    const isCurrentOrganizationOnboardedWithDeepSign = computed(
      () =>
        isCurrentOrganizationOnboardedServiceName.value ===
        ONBOARDING_QUERY_TYPES.ONBOARDING_QUERY_TYPE_DEEPSIGN,
    )

    const isCurrentOrganizationOnlyService = computed(
      () =>
        isCurrentOrganizationOnboardedWithDeepSign.value ||
        isCurrentOrganizationOnboardedWithDeepV.value,
    )

    const isCurrentOrganizationPersonal = computed(() =>
      helpers.isPersonalSpace(toValue(currentOrganization)),
    )

    const isCurrentOrganizationOnboardedWithDeepVAndNone = computed(
      () =>
        isCurrentOrganizationOnboardedWithDeepV.value &&
        currentOrganizationVerificationStatus.value ===
          VERIFICATION_STATES.VERIFICATION_STATE_NONE,
    )

    const isNoBoxOrganization = computed(() => {
      if (toValue(currentOrganization)) {
        return (
          currentOrganizationVerificationStatus.value ===
          VERIFICATION_STATES.VERIFICATION_STATE_NONE
        )
      }
      return false
    })

    const showCreateOwnBoxCard = computed(
      () =>
        isNoBoxOrganization.value &&
        currentOrganizationVerificationStatus.value ===
          VERIFICATION_STATES.VERIFICATION_STATE_NONE &&
        currentOrganizationVerificationProcessStatus.value ===
          VERIFICATION_PROCESS_TYPES.VERIFICATION_PROCESS_TYPE_ERROR,
    )

    const isNoBoxOrganizationNoVerificationOngoing = computed(
      () =>
        !!toValue(currentOrganization) &&
        currentOrganizationVerificationStatus.value ===
          VERIFICATION_STATES.VERIFICATION_STATE_NONE &&
        currentOrganizationVerificationProcessStatus.value === null,
    )

    return {
      currentOrganizationOnboardingVerificationStatus,
      isCurrentOrganizationPersonalSpaceVerificationInProgress,
      isCurrentOrganizationInOnboardingVerifyProcess,
      isCurrentOrganizationOnlyService,
      isCurrentOrganizationPersonal,
      currentOrganizationVerificationStatus,
      isCurrentOrganizationOnboardedWithDeepVAndInProgress,
      isCurrentOrganizationOnboardedWithDeepSign,
      isCurrentOrganizationOnboardedWithDeepV,
      currentOrganizationVerificationProcessStatus,
      isNoBoxOrganization,
      isNoBoxOrganizationNoVerificationOngoing,
      isCurrentOrganizationVerifiedWeak,
      isCurrentOrganizationVerifiedStrong,
      isCurrentOrganizationVerified,
      showCreateOwnBoxCard,
      currentOrganizationOnboardingVerificationError,
    }
  },
)
